// Tax Flow — shared currency PRESENTATION helpers (dual-currency redesign).
// STRICTLY presentation-layer: canonical price/currency stay the source of truth; the
// equivalent is always computed dynamically from the firm's configurable USD⇄THB rate.
// USD is the primary display for everyone; the THB equivalent is shown only when appropriate
// (Thai tax residents / Thai citizens — driven by `show_thb` from the backend context).
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export const CUR_SYMBOL = { USD: "$", THB: "฿", EUR: "€", GBP: "£", SGD: "S$", AUD: "A$", CAD: "C$" };
export const DEFAULT_USD_THB_RATE = 33.0;
export const FX_DISCLAIMER =
  "Exchange rate is a daily institutional reference from the Bank of Thailand. " +
  "The equivalent shown is informational only. " +
  "Your invoice denomination is not changed by your settlement currency choice.";

const _fmt = (n, decimals = 0) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

// One amount in a specific currency, e.g. "$450" / "฿14,850" / "1,200 SGD".
export function fmtAmount(value, currency = "USD", { decimals } = {}) {
  const cur = currency || "USD";
  const sym = CUR_SYMBOL[cur];
  const dp = decimals != null ? decimals : cur === "THB" ? 0 : 0;
  const v = _fmt(value, dp);
  return sym ? `${sym}${v}` : `${v} ${cur}`;
}

// Convert between currencies for PRESENTATION only (USD⇄THB via the firm rate; identity otherwise).
export function toUSD(value, currency, rate) {
  const r = rate || DEFAULT_USD_THB_RATE;
  if (currency === "USD" || !currency) return Number(value || 0);
  if (currency === "THB") return Number(value || 0) / r;
  return Number(value || 0); // other currencies: no conversion table — shown as-is (canonical only)
}
export function toTHB(value, currency, rate) {
  const r = rate || DEFAULT_USD_THB_RATE;
  if (currency === "THB") return Number(value || 0);
  if (currency === "USD" || !currency) return Number(value || 0) * r;
  return null; // no THB equivalent for non-USD/THB canonical currencies
}

// The equivalent line for one canonical amount, respecting the "USD primary / THB when appropriate" rule:
//  - canonical THB  → always show the USD equivalent (everyone sees USD).
//  - canonical USD  → show the THB equivalent ONLY when showThb (Thai client).
//  - other currency → no equivalent.
// Returns the formatted equivalent string, or null when none should be shown.
export function equivalentFor(value, currency, { rate, showThb } = {}) {
  const r = rate || DEFAULT_USD_THB_RATE;
  const cur = currency || "USD";
  if (cur === "THB") return fmtAmount(toUSD(value, "THB", r), "USD") + " USD";
  if (cur === "USD") return showThb ? fmtAmount(toTHB(value, "USD", r), "THB") : null;
  return null;
}

// Format rate_as_of date string for display ("as of Jun 10, 2025").
export function formatRateDate(rateAsOf) {
  if (!rateAsOf) return null;
  try {
    // BOT date format is "YYYY-MM-DD"; handle both date-only and ISO datetime
    const d = new Date(rateAsOf.length === 10 ? rateAsOf + "T12:00:00Z" : rateAsOf);
    if (isNaN(d.getTime())) return rateAsOf;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return rateAsOf;
  }
}

// React hook: fetch the firm's currency display context once (rate + show_thb for a client).
// Falls back safely to the default rate and USD-only if the request fails.
export function useCurrencyContext(clientId) {
  const [ctx, setCtx] = useState({
    usd_thb_rate: DEFAULT_USD_THB_RATE,
    show_thb: false,
    fx_source: null,
    rate_as_of: null,
    disclaimer: FX_DISCLAIMER,
  });
  useEffect(() => {
    let alive = true;
    const q = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
    api.get(`/currency/display-context${q}`)
      .then((r) => { if (alive && r?.data) setCtx(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [clientId]);
  return ctx;
}
